import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = [
  'Portfolio Analysis',
  'Portfolio Analysis - Data',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      updatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPut:
        build.mutation<
          UpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutApiResponse,
          UpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutApiArg
        >({
          query: (queryArg) => ({
            url: `/update_portfolio_analysis_now/${queryArg.accountId}`,
            method: 'PUT',
          }),
          invalidatesTags: ['Portfolio Analysis'],
        }),
      getPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGet:
        build.query<
          GetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetApiResponse,
          GetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/portfolio_analysis/${queryArg.analysisType}/${queryArg.accountId}`,
            params: {
              degree_day_correction: queryArg.degreeDayCorrection,
            },
          }),
          providesTags: ['Portfolio Analysis'],
        }),
      hourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGet:
        build.query<
          HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse,
          HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/hour_analysis_portfolio/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
            params: {
              start_timestamp: queryArg.startTimestamp,
              end_timestamp: queryArg.endTimestamp,
              active_days_filter: queryArg.activeDaysFilter,
              holidays_filter: queryArg.holidaysFilter,
            },
          }),
          providesTags: ['Portfolio Analysis - Data'],
        }),
      durationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGet:
        build.query<
          DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse,
          DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/duration_analysis_portfolio/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
            params: {
              start_timestamp: queryArg.startTimestamp,
              end_timestamp: queryArg.endTimestamp,
              active_period_filter: queryArg.activePeriodFilter,
              holidays_filter: queryArg.holidaysFilter,
            },
          }),
          providesTags: ['Portfolio Analysis - Data'],
        }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type UpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutApiResponse =
  /** status 200 Successful Response */ number;
export type UpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutApiArg =
  {
    /** The ReCalc account/subscription id to update. */
    accountId: number;
  };
export type GetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetApiResponse =
  /** status 200 Successful Response */ PortfolioAnalysisOutput;
export type GetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetApiArg =
  {
    /** The ReCalc account id to analyse. */
    accountId: number;
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** Whether to apply degree day correction to the analysis. Default is False. Only applies to heat analysis. */
    degreeDayCorrection?: boolean;
  };
export type HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ HourAnalysisOutput;
export type HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg =
  {
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** The ReCalc account id to get the data for. */
    accountId: number;
    /** The meter id to get the data for. */
    meterId: number;
    /** Start of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    startTimestamp: string;
    /** End of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    endTimestamp: string;
    /** Filter out data points where the timestamp is outside the active days if True. If False the data points inside the active days will be filtered out. If None no filtering will be done. */
    activeDaysFilter?: boolean | null;
    /** Filter out data points where the timestamp is not on a holiday if True. If False the data points on a holiday will be filtered out. If None no filtering will be done. */
    holidaysFilter?: boolean | null;
  };
export type DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ DurationAnalysisOutput;
export type DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg =
  {
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** The ReCalc account id to get the data for. */
    accountId: number;
    /** The meter id to get the data for. */
    meterId: number;
    /** Start of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    startTimestamp: string;
    /** End of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    endTimestamp: string;
    /** Filter data points based on opening hours. If True, keep only data within opening hours (active period). If False, keep only data outside opening hours (inactive period). If None, no filtering is applied. */
    activePeriodFilter?: boolean | null;
    /** Filter out data points where the timestamp is not on a holiday if True. If False the data points on a holiday will be filtered out. If None no filtering will be done. */
    holidaysFilter?: boolean | null;
  };
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type OccupancyDay = {
  /** If there is occupancy on this day. */
  occupancy: boolean;
  /** The start time of occupancy. */
  start_time?: number | null;
  /** The end time of occupancy. */
  end_time?: number | null;
};
export type PortfolioAnalysisOccupancy = {
  /** Occupancy information for Monday. */
  Monday: OccupancyDay;
  /** Occupancy information for Tuesday. */
  Tuesday: OccupancyDay;
  /** Occupancy information for Wednesday. */
  Wednesday: OccupancyDay;
  /** Occupancy information for Thursday. */
  Thursday: OccupancyDay;
  /** Occupancy information for Friday. */
  Friday: OccupancyDay;
  /** Occupancy information for Saturday. */
  Saturday: OccupancyDay;
  /** Occupancy information for Sunday. */
  Sunday: OccupancyDay;
};
export type PortfolioAnalysisResult = {
  /** The address of the meter. */
  address: string;
  /** The name of the meter. */
  name?: string | null;
  /** The display name of the meter. */
  display_name?: string | null;
  /** The main address of the matrikel. */
  main_address?: string;
  /** The matrikel key of the meter. */
  matrikel_key?: string | null;
  /** The type of metering point (MP) of the meter. */
  type_of_mp?: string | null;
  /** The start of the daytime period. */
  daytime_start?: number | null;
  /** The end of the daytime period. */
  daytime_end?: number | null;
  /** The average usage. */
  average_all?: number | null;
  /** The average day usage. */
  average_day?: number | null;
  /** The average night usage. */
  average_night?: number | null;
  /** The average weekday daytime usage. */
  average_weekday_daytime?: number | null;
  /** The average full day usage for the weekends. */
  average_weekend_full_day?: number | null;
  /** The weekend weekday ratio. */
  weekend_weekday_ratio?: number | null;
  /** The weekend night weekday night ratio. */
  weekend_night_weekday_night_ratio?: number | null;
  /** The average base load. */
  average_base_load?: number | null;
  /** The average peak load. */
  average_peak_load?: number | null;
  /** The average weekday base load. */
  average_weekday_base_load?: number | null;
  /** The average weekend base load. */
  average_weekend_base_load?: number | null;
  /** The average weekday peak load. */
  average_weekday_peak_load?: number | null;
  /** The average weekend peak load. */
  average_weekend_peak_load?: number | null;
  /** The yearly trend. */
  yearly_trend?: number | null;
  /** The average usage for the last 3 years. */
  average_last_3_years?: number | null;
  /** The year over year ratio. */
  yoy_ratio?: number | null;
  /** The previous year ratio. */
  previous_year_ratio?: number | null;
  /** The year over year ratio for weekday nights. */
  yoy_ratio_weekday_nights?: number | null;
  /** The previous year ratio for weekday nights. */
  previous_year_ratio_weekday_nights?: number | null;
  /** The year over year ratio for weekdays. */
  yoy_ratio_weekdays?: number | null;
  /** The previous year ratio for weekdays. */
  previous_year_ratio_weekdays?: number | null;
  /** The year over year ratio for weekends. */
  yoy_ratio_weekends?: number | null;
  /** The previous year ratio for weekends. */
  previous_year_ratio_weekends?: number | null;
  /** The data stream info of the meter. */
  data_stream?: object | null;
  /** The data stream info for net supply. */
  data_stream_net_supply?: object | null;
  /** The data stream info for consumption from grid. */
  data_stream_consumption_from_grid?: object | null;
  /** The data stream info for net production. */
  data_stream_net_production?: object | null;
  /** The data stream info for own production. */
  data_stream_own_production?: object | null;
  /** The location code of the meter (either DK1 or DK2). */
  location_code?: string | null;
  /** The total kWh for the year. */
  total_consumption_year?: number | null;
  /** The total kWh for the last year. */
  total_consumption_last_year?: number | null;
  /** The total CO2e for the year in kg. */
  total_co2e_year?: number | null;
  /** The total CO2e for the last year in kg. */
  total_co2e_last_year?: number | null;
  /** The average daily consumption in kWh. */
  average_daily_consumption?: number | null;
  /** The peak consumption in kW. */
  peak?: number | null;
  /** The average full day usage for weekdays in kWh. */
  average_weekday_full_day?: number | null;
  /** The average usage for the last year (12-24 months) in kW. */
  average_last_year?: number | null;
  /** The average usage for the last year (12-24 months) in kWh. */
  average_all_last_year?: number | null;
  /** The total kWh for the previous year. */
  total_consumption_previous_year?: number | null;
  /** The day night ratio. */
  day_night_ratio?: number | null;
  /** The night day ratio. */
  night_day_ratio?: number | null;
  /** The average usage between 17:00 and 21:00 in kW. */
  average_17_to_21?: number | null;
  /** The average weekday day usage in kW. */
  average_weekday_day?: number | null;
  /** The average weekend day usage in kW. */
  average_weekend_day?: number | null;
  /** The average weekend night usage in kW. */
  average_weekend_night?: number | null;
  /** The average weekday night usage in kW. */
  average_weekday_night?: number | null;
  /** The average usage for the inactive hours in kW. */
  average_all_inactive_hours?: number | null;
  /** The inactive active ratio. */
  inactive_active_ratio?: number | null;
  /** The start time of the data. */
  data_start_time?: string | null;
  /** The end time of the data. */
  data_end_time?: string | null;
  /** The source of the daytime period. */
  daytime_period_source?: string;
  /** The occupancy info of the meter . */
  occupancy?: PortfolioAnalysisOccupancy | null;
  /** Current calendar year average. */
  current_calendar_year_avg?: number | null;
  /** Last calendar year average. */
  last_calendar_year_avg?: number | null;
  /** Previous calendar year average. */
  previous_calendar_year_avg?: number | null;
  /** Previous calendar year sum. */
  previous_calendar_year_sum?: number | null;
  /** The metering point id. */
  metering_point_id?: string | null;
  /** Current calendar year sum. */
  current_calendar_year_sum?: number | null;
  /** Last calendar year sum. */
  last_calendar_year_sum?: number | null;
  /** Year over year ratio for current calendar year vs last. */
  yoy_ratio_current_calendar_year_vs_last?: number | null;
  /** The percentage of data available for the last 1 year. */
  one_year_data_pct?: number | null;
  /** The percentage of data available for the last 2 years. */
  two_year_data_pct?: number | null;
  /** The percentage of data available for the last 3 years. */
  three_year_data_pct?: number | null;
  /** The percentage of data available for the 12 to 24 months period. */
  data_pct_12_to_24_months?: number | null;
  /** The percentage of data available for the 24 to 36 months period. */
  data_pct_24_to_36_months?: number | null;
  /** The average usage during the heating season. */
  average_heating_season?: number | null;
  /** The average usage during the non-heating season. */
  average_non_heating_season?: number | null;
  /** The average usage during the heating season active hours. */
  average_heating_season_active?: number | null;
  /** The average usage during the non-heating season active hours. */
  average_non_heating_season_active?: number | null;
  /** The average usage during the heating season inactive hours. */
  average_heating_season_inactive?: number | null;
  /** The average usage during the non-heating season inactive hours. */
  average_non_heating_season_inactive?: number | null;
  /** The yearly trend for the active hours. */
  yearly_trend_active?: number | null;
  /** The yearly trend for the inactive hours. */
  yearly_trend_inactive?: number | null;
  /** The yearly trend for data within the heating season. */
  heating_season_trend?: number | null;
  /** The yearly trend for data within the heating season during the active hours. */
  heating_season_trend_active?: number | null;
  /** The yearly trend for data within the heating season during the inactive hours. */
  heating_season_trend_inactive?: number | null;
  /** The average usage for the last 30 days. */
  last_30_days_average?: number | null;
  /** The average usage for the last 30 days during the active period. */
  last_30_days_active_period?: number | null;
  /** The average usage for the last 30 days during the inactive period. */
  last_30_days_inactive_period?: number | null;
  /** The trend for the last 30 days. */
  last_30_day_trend?: number | null;
  /** The percentage of data available for the last 30 days. */
  thirty_day_data_pct?: number | null;
  /** PV net production for the year in kWh (TypeOfMP D05). */
  pv_net_production_year?: number | null;
  /** PV net production average in kW (TypeOfMP D05). */
  pv_net_production_avg?: number | null;
  /** PV CO2e savings for the year in kg (TypeOfMP D05). */
  pv_co2e_savings_year?: number | null;
  /** PV supplied to grid for the year in kWh (TypeOfMP D06). */
  pv_supplied_to_grid_year?: number | null;
  /** PV supplied to grid average in kW (TypeOfMP D06). */
  pv_supplied_to_grid_avg?: number | null;
  /** PV own production for the year in kWh (TypeOfMP D09). */
  pv_own_production_year?: number | null;
  /** PV own production average in kW (TypeOfMP D09). */
  pv_own_production_avg?: number | null;
  /** PV consumption from grid for the year in kWh (TypeOfMP D07). */
  pv_consumption_from_grid_year?: number | null;
  /** PV consumption from grid average in kW (TypeOfMP D07). */
  pv_consumption_from_grid_avg?: number | null;
  /** The average forward flow temperature for the last 30 days. */
  forward_flow_temp_avg_last_30_days?: number | null;
  /** The average return flow temperature for the last 30 days. */
  return_flow_temp_avg_last_30_days?: number | null;
  /** The average forward flow temperature for the year. */
  forward_flow_temp_avg_year?: number | null;
  /** The average return flow temperature for the year. */
  return_flow_temp_avg_year?: number | null;
  /** The average delta temperature for the last 30 days. */
  delta_temp_avg_last_30_days?: number | null;
  /** The average delta temperature for the year. */
  delta_temp_avg_year?: number | null;
  /** The delta temperature trend for the last 30 days. */
  delta_temp_trend_last_30_days?: number | null;
  /** The delta temperature trend for the year. */
  delta_temp_trend_year?: number | null;
  /** Consumption-weighted average spot electricity price for the latest 12 months. */
  average_spot_price?: number | null;
  /** Consumption-weighted average of all tariffs including grid fees and system tariffs. */
  average_total_tariffs?: number | null;
  /** Consumption-weighted average complete electricity price including spot prices and all tariffs. */
  average_total_price?: number | null;
  /** Total cost from spot market prices for the year, excluding tariffs and fees. */
  total_spot_cost_year?: number | null;
  /** Total fees paid for the year including consumption-based tariffs and subscription fees. */
  total_fees_year?: number | null;
  /** Total electricity cost for the year including spot costs, tariffs, and subscription fees. */
  total_cost_year?: number | null;
  /** Consumption-weighted average price during active hours according to occupancy schedule. */
  average_daytime_price?: number | null;
  /** Consumption-weighted average price during inactive hours according to occupancy schedule. */
  average_nighttime_price?: number | null;
  /** Consumption-weighted average price during active days according to occupancy schedule. */
  average_weekday_price?: number | null;
  /** Consumption-weighted average price during inactive days according to occupancy schedule. */
  average_weekend_price?: number | null;
  /** Consumption-weighted average total price per kWh for year 2 (12-24 months ago). */
  average_price_last_year?: number | null;
  /** Consumption-weighted average total price per kWh for year 1 (24-36 months ago). */
  average_price_previous_year?: number | null;
  /** Total cost from spot market prices for year 2, excluding tariffs and fees. */
  total_spot_cost_last_year?: number | null;
  /** Total cost from spot market prices for year 1, excluding tariffs and fees. */
  total_spot_cost_previous_year?: number | null;
  /** Total fees paid for year 2 including consumption-based tariffs and subscription fees. */
  total_fees_last_year?: number | null;
  /** Total fees paid for year 1 including consumption-based tariffs and subscription fees. */
  total_fees_previous_year?: number | null;
  /** Total electricity cost for year 2 including spot costs, tariffs, and subscription fees. */
  total_cost_last_year?: number | null;
  /** Total electricity cost for year 1 including spot costs, tariffs, and subscription fees. */
  total_cost_previous_year?: number | null;
  /** Consumption-weighted average total price per kWh for the current calendar year. */
  current_calendar_year_price?: number | null;
  /** Consumption-weighted average total price per kWh for the previous calendar year. */
  last_calendar_year_price?: number | null;
  /** Consumption-weighted average total price per kWh for the calendar year before last. */
  previous_calendar_year_price?: number | null;
  /** Total cost from spot market prices for the current calendar year. */
  current_calendar_year_spot_cost?: number | null;
  /** Total cost from spot market prices for the previous calendar year. */
  last_calendar_year_spot_cost?: number | null;
  /** Total cost from spot market prices for the calendar year before last. */
  previous_calendar_year_spot_cost?: number | null;
  /** Total fees for current calendar year including consumption-based tariffs and subscription fees. */
  current_calendar_year_fees?: number | null;
  /** Total fees for previous calendar year including consumption-based tariffs and subscription fees. */
  last_calendar_year_fees?: number | null;
  /** Total fees for calendar year before last including tariffs and subscription fees. */
  previous_calendar_year_fees?: number | null;
  /** Total electricity cost for current calendar year including spot costs, tariffs, and fees. */
  current_calendar_year_cost?: number | null;
  /** Total electricity cost for previous calendar year including spot costs, tariffs, and fees. */
  last_calendar_year_cost?: number | null;
  /** Total electricity cost for calendar year before last including spot costs, tariffs, and fees. */
  previous_calendar_year_cost?: number | null;
};
export type TimeSeriesDataPoint = {
  /**
        The timestamp representing the value in the time series.
        Format: 2022-08-23T13:10:27+00:00
        where +00:00 is the timezone
         */
  timestamp: string;
  /**
        The corresponding value at the timestamp
         */
  value: number;
};
export type ApiWarning = {
  /** The warning message */
  message: string;
  /** The warning type */
  warning_type: string;
  /** The warning details */
  details?: object;
};
export type PortfolioAnalysisOutput = {
  /** List of portfolio analysis results. */
  portfolio_analysis_list: PortfolioAnalysisResult[];
  /** List of summed data for the portfolio analysis. */
  summed_data: TimeSeriesDataPoint[];
  /** List of warnings. */
  warnings: ApiWarning[];
};
export type AnalysisType = 'power' | 'heat' | 'water';
export type TimestampsSummaryModel = {
  /** The minimum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minEarliestDataTimestamp: string | null;
  /** The maximum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxEarliestDataTimestamp: string | null;
  /** The minimum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minLatestDataTimestamp: string | null;
  /** The maximum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxLatestDataTimestamp: string | null;
};
export type HourAnalysisDataPoint = {
  /** The hour of the day. */
  HourOfDay: number;
  /** The min value of the data. */
  MinValue: number;
  /** The max value of the data. */
  MaxValue: number;
  /** The average value of the data. */
  MeanValue: number;
  /** The median of the data. */
  MedianValue: number;
  /** The first quartile of the data. */
  Q1Value: number;
  /** The third quartile of the data. */
  Q3Value: number;
};
export type HourAnalysisOutput = {
  /** The Id of the associated entity. */
  Id?: number | string | null;
  /** The name of the associated entity. */
  Name: string;
  /** The timestamp summary. */
  TimestampsSummary: TimestampsSummaryModel;
  /** The hour analysis data. */
  HourAnalysisData: HourAnalysisDataPoint[];
  Warnings?: ApiWarning[];
};
export type DurationAnalysisDataPoint = {
  /** The duration in hours. */
  Duration: number;
  /** The value of the data. */
  Value: number;
  /** The timestamp of the data point. Format: YYYY-MM-DDTHH:MM:SSZ */
  Timestamp: string;
};
export type DurationLoadProfileShare = {
  /** Share of time in hours or percentage of hours where the load profile contains energy usage belonging to active hours and active days. Null if openingHours is not configured. */
  ActivePeriod?: number | null;
  /** Share of time in hours or percentage of hours where the load profile contains energy usage belonging to outside active hours and active days. Null if openingHours is not configured. */
  InactivePeriod?: number | null;
};
export type DurationLoadProfilePoint = {
  /** The name of the load profile point. */
  Name: string;
  /** The value of the load profile point. */
  Value: number;
  /** The start duration in hours. */
  StartDuration: number;
  /** The end duration in hours. */
  EndDuration: number;
  /** The shares of the load profile in hours */
  Shares: DurationLoadProfileShare;
  /** The shares of the load profile in percentage */
  SharesPercentage: DurationLoadProfileShare;
};
export type DurationAnalysisOutput = {
  /** The Id of the associated entity. */
  Id?: number | string | null;
  /** The name of the associated entity. */
  Name: string;
  /** The timestamp summary. */
  TimestampsSummary: TimestampsSummaryModel;
  /** The duration analysis data. */
  DurationAnalysisData: DurationAnalysisDataPoint[];
  /** The load profiles for the duration analysis. */
  LoadProfiles: DurationLoadProfilePoint[];
  Warnings?: ApiWarning[];
};
export const {
  useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation,
  useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery,
  useHourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
  useDurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
} = injectedRtkApi;
